// The note body: one <section> per NoteSection, blocks rendered in order. Bullets go through
// RichText note mode (code spans on, no band anchors). Within a section, ≥2 code blocks collapse
// into ONE local tab group at the first code block's position (embed-E mockup); a lone code block
// renders plainly. Unknown section kinds and bulletless blocks render generically (forward compat —
// a newer backend must never blank this view).

import { useState } from 'react';
import type { NoteActionItem, NoteBlock, NoteSection } from '../../lib/note-doc';
import { sectionLabel } from '../../lib/note-doc';
import { RichText } from '../brief/richText';
import { NoteTableView } from './NoteTable';
import { NoteChartView } from './NoteChart';

export interface OutlineEntry { id: string; label: string; sub: boolean }

// Sections + their subheadings, with the anchor ids the renderer emits. Subheading ordinal = count
// of subheaded blocks so far in the section (stable even when blocks mix subheaded/plain).
export function outlineEntries(sections: NoteSection[]): OutlineEntry[] {
  const entries: OutlineEntry[] = [];
  for (const s of sections) {
    entries.push({ id: `nd-sec-${s.kind}`, label: sectionLabel(s.kind), sub: false });
    let sub = 0;
    for (const b of s.blocks ?? []) {
      if (b?.subheading) entries.push({ id: `nd-sub-${s.kind}-${sub++}`, label: b.subheading, sub: true });
    }
  }
  return entries;
}

interface CodeTab { label: string; id?: string; code: { lang?: string | null; text: string } }

function CodeTabs({ tabs }: { tabs: CodeTab[] }) {
  const [active, setActive] = useState(0);
  const current = tabs[active] ?? tabs[0];
  return (
    <div className="nd-codegroup">
      <div className="nd-codetabs">
        {tabs.map((t, i) => (
          <button key={i} id={t.id} type="button"
            className={`nd-codetab${i === active ? ' is-active' : ''}`}
            onClick={() => setActive(i)}>
            {t.label}
          </button>
        ))}
      </div>
      <pre className="nd-code"><code>{current.code.text}</code></pre>
    </div>
  );
}

function Bullets({ bullets, onNav }: { bullets: string[]; onNav: (id: string) => void }) {
  return (
    <ul className="nd-ul">
      {bullets.map((b, i) => <li key={i}><RichText text={b} onNav={onNav} codeSpans /></li>)}
    </ul>
  );
}

// Structured action items: the text plus an owner chip and a due tag when the source stated them.
// Rendered instead of the block's `bullets` (which carry the same texts) so nothing shows twice.
function ActionItems({ items, onNav }: { items: NoteActionItem[]; onNav: (id: string) => void }) {
  return (
    <ul className="nd-actions">
      {items.map((a, i) => (
        <li key={i} className="nd-action">
          <span className="nd-action-text"><RichText text={a.text} onNav={onNav} codeSpans /></span>
          {a.owner && <span className="nd-action-owner">{a.owner}</span>}
          {a.due && <span className="nd-action-due">{a.due}</span>}
        </li>
      ))}
    </ul>
  );
}

export function NoteSections({ sections, onNav }: { sections: NoteSection[]; onNav: (id: string) => void }) {
  return (
    <div className="nd-sections">
      {sections.map((section) => {
        const blocks = Array.isArray(section.blocks) ? section.blocks : [];
        const codeIdxs = blocks.map((b, i) => (b?.code ? i : -1)).filter(i => i >= 0);
        const grouped = codeIdxs.length >= 2;
        // Subheading ordinal per block (mirrors outlineEntries).
        let subOrdinal = 0;
        const subIdOf = new Map<number, string>();
        blocks.forEach((b, i) => { if (b?.subheading) subIdOf.set(i, `nd-sub-${section.kind}-${subOrdinal++}`); });
        const tabs: CodeTab[] = grouped
          ? codeIdxs.map((i) => ({
              label: blocks[i].subheading ?? blocks[i].code?.lang ?? `Block ${i + 1}`,
              id: subIdOf.get(i),
              code: blocks[i].code!,
            }))
          : [];
        return (
          <section key={section.kind} id={`nd-sec-${section.kind}`} className="nd-sec">
            <h2>{sectionLabel(section.kind)}</h2>
            {blocks.map((block: NoteBlock, i) => {
              const partOfGroup = grouped && block?.code;
              const isGroupAnchor = partOfGroup && i === codeIdxs[0];
              return (
                <div key={i} className="nd-block">
                  {/* Grouped code blocks surrender their subheading to the tab label. */}
                  {block?.subheading && !partOfGroup && (
                    <h3 id={subIdOf.get(i)} className="nd-sub">{block.subheading}</h3>
                  )}
                  {/* Structured action items win over bullets (they carry the same texts + owner/due). */}
                  {Array.isArray(block?.action_items) && block.action_items.length > 0 ? (
                    <ActionItems items={block.action_items} onNav={onNav} />
                  ) : (
                    Array.isArray(block?.bullets) && block.bullets.length > 0 && (
                      <Bullets bullets={block.bullets} onNav={onNav} />
                    )
                  )}
                  {block?.code && !grouped && (
                    <pre className="nd-code"><code>{block.code.text}</code></pre>
                  )}
                  {isGroupAnchor && <CodeTabs tabs={tabs} />}
                  {block?.table && <NoteTableView table={block.table} />}
                  {block?.chart && <NoteChartView chart={block.chart} />}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
